import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { type EmployeeJoin, joinErrorInfo, joinMeeting, useRoomAccessStore } from '@/entities/call'
import { meetingKeys } from '@/entities/meeting'
import { useSession } from '@/entities/session'
import { toast } from '@/shared/ui/toast'

// Сотрудник входит в свою встречу: room-JWT в память → переход в комнату.
// В закрытом звонке вход возвращает waiting — комнаты ещё нет, ждём допуска.
// notifyErrors=false — для фонового опроса допуска: там отказ ожидаем и экран
// уже объясняет, что происходит.
export function useJoinMeeting(notifyErrors = true) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setRoomAccess = useRoomAccessStore((s) => s.setRoomAccess)
  const { session } = useSession()

  return useMutation({
    mutationFn: (meetingId: string) => joinMeeting(meetingId),
    onSuccess: (res: EmployeeJoin) => {
      if (!res.roomAccess) return // waiting — экран ожидания продолжит опрос
      // Заказчику общая витрина закрыта гейтом: вернём сразу на его собственную,
      // иначе выход из звонка идёт лишним прыжком через редирект.
      setRoomAccess(res.roomAccess, session?.kind === 'customer' ? '/my/meetings' : '/meetings')
      void navigate({ to: '/room/$id', params: { id: res.roomAccess.meetingId } })
    },
    onError: (error, meetingId) => {
      const info = joinErrorInfo(error)
      // Терминальный отказ (встречу завершили между экраном и кнопкой) —
      // обновляем деталь: prejoin сам покажет полноэкранный статус.
      if (info.kind === 'ended' || info.kind === 'cancelled' || info.kind === 'not-found') {
        void queryClient.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) })
      }
      if (notifyErrors) toast.error(info.message)
    },
  })
}

const ADMISSION_POLL_MS = 3000

// Поток входа для prejoin: клик → либо комната, либо ожидание допуска в закрытый
// звонок. Ожидание опрашивается тем же идемпотентным join'ом — пока waiting он
// возвращает то же состояние, после допуска отдаёт room-JWT и уводит в комнату.
// Отдельной ручки статуса для сотрудника нет, как у гостя.
export function useJoinFlow(meetingId: string) {
  const [waiting, setWaiting] = useState(false)
  // Пока ждём допуска, отказы опроса не тостим: сообщение раз в 3 секунды — шум,
  // а экран ожидания и так объясняет происходящее.
  const join = useJoinMeeting(!waiting)
  const { mutate } = join

  function start() {
    mutate(meetingId, { onSuccess: (res) => setWaiting(res.state === 'waiting') })
  }

  useEffect(() => {
    if (!waiting) return
    const timer = setInterval(() => {
      mutate(meetingId, {
        onSuccess: (res) => setWaiting(res.state === 'waiting'),
        // Встречу завершили, отменили или удалили, пока человек ждал: снимаем
        // ожидание, и prejoin показывает полноэкранный статус вместо вечного
        // «Ждём допуска». Прочие отказы (сеть моргнула) — просто следующая попытка.
        onError: (error) => {
          const info = joinErrorInfo(error)
          if (info.kind === 'ended' || info.kind === 'cancelled' || info.kind === 'not-found') {
            setWaiting(false)
          }
        },
      })
    }, ADMISSION_POLL_MS)
    return () => clearInterval(timer)
  }, [waiting, meetingId, mutate])

  return { start, waiting, isPending: join.isPending }
}
